import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik, FieldArray } from "formik";
import * as Yup from "yup";
import {
  Button,
  Segment,
  Grid,
  Header,
  Message,
  Icon,
  Divider,
  Card,
  Input,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import ModalWrapper from "../../app/common/modals/modalWrapper";
import { closeModal } from "../../app/common/modals/modalSlice";
import MyTextInput from "../../app/common/form/MyTextInput";
import MySelectInput from "../../app/common/form/MySelectInput";
import MyCheckbox from "../../app/common/form/MyCheckbox";
import {
  addComplianceTemplateToDb,
  updateComplianceTemplateInDb,
} from "../../app/firestore/firestoreService";
import {
  tableForDeadlineTemplates,
} from "../../app/common/categoryData/categoryOptions";
import {
  conditionNeedsValue,
  conditionalBasedOnOptions,
  getConditionalDisplayText,
  getConditionalValueLabel,
  getConditionalValueOptions,
} from "../compliance/complianceConditionUtils";

const userStateOptions = [
  { key: "Colorado", value: "Colorado", text: "Colorado" },
  { key: "California", value: "California", text: "California" },
  { key: "Texas", value: "Texas", text: "Texas" },
  { key: "Florida", value: "Florida", text: "Florida" },
  { key: "Arizona", value: "Arizona", text: "Arizona" },
  { key: "Nevada", value: "Nevada", text: "Nevada" },
  { key: "Washington", value: "Washington", text: "Washington" },
  { key: "Oregon", value: "Oregon", text: "Oregon" },
  { key: "Utah", value: "Utah", text: "Utah" },
  { key: "Idaho", value: "Idaho", text: "Idaho" },
  { key: "Montana", value: "Montana", text: "Montana" },
  { key: "Wyoming", value: "Wyoming", text: "Wyoming" },
  { key: "NewMexico", value: "NewMexico", text: "New Mexico" },
];

const requirementOptions = [
  { key: "required", value: "required", text: "Required" },
  { key: "optional", value: "optional", text: "Optional" },
  { key: "conditional", value: "conditional", text: "Conditional" },
];

const agentRepresentsOptions = [
  { key: "Buyer", value: "Buyer", text: "Buyer" },
  { key: "Seller", value: "Seller", text: "Seller" },
  { key: "Landlord", value: "Landlord", text: "Landlord" },
  { key: "Tenant", value: "Tenant", text: "Tenant" },
];

const propertyTypeOptions = [
  { key: "All", value: "All", text: "All Property Types" },
  { key: "Residential", value: "Residential", text: "Residential" },
  { key: "Commercial", value: "Commercial", text: "Commercial" },
  { key: "Land", value: "Land", text: "Land" },
  { key: "Foreclosure", value: "Foreclosure", text: "Foreclosure" },
  {
    key: "Residential Income",
    value: "Residential Income",
    text: "Residential Income",
  },
];

const conditionalDateOptions = tableForDeadlineTemplates
  .filter((deadline) => !deadline.isTopic && deadline.name)
  .map((deadline) => ({
    key: deadline.name,
    value: deadline.name,
    text: deadline.event,
  }));

export default function AdminComplianceTemplateModal({
  existingTemplate = null,
  isEditMode = false,
  prefilledBrokerageId = "",
  prefilledStateProvince = "",
  lockBrokerageId = false,
  lockStateProvince = false,
  prefillFromTemplate = null,
}) {
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [formMessage, setFormMessage] = useState("");
  const [formMessageType, setFormMessageType] = useState("");
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  const getInitialValues = () => {
    if (isEditMode && existingTemplate) {
      return {
        name: existingTemplate.name || "",
        stateProvince: existingTemplate.stateProvince || "Colorado",
        agentRepresents: existingTemplate.agentRepresents || "",
        brokerageId: existingTemplate.brokerageId || "",
        isDefault: existingTemplate.isDefault || false,
        categories: existingTemplate.categories || [],
      };
    }
    if (prefillFromTemplate) {
      return {
        name: prefillFromTemplate.name
          ? `${prefillFromTemplate.name} (Copy)`
          : "",
        stateProvince:
          prefilledStateProvince ||
          prefillFromTemplate.stateProvince ||
          "Colorado",
        agentRepresents: prefillFromTemplate.agentRepresents || "",
        brokerageId:
          prefilledBrokerageId || prefillFromTemplate.brokerageId || "",
        isDefault: prefilledBrokerageId ? false : false,
        categories: JSON.parse(
          JSON.stringify(prefillFromTemplate.categories || [])
        ),
      };
    }
    return {
      name: "",
      stateProvince: prefilledStateProvince || "Colorado",
      agentRepresents: "",
      brokerageId: prefilledBrokerageId || "",
      isDefault: prefilledBrokerageId ? false : true,
      categories: [],
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setFormMessage("");
    try {
      const templateData = {
        name: values.name,
        stateProvince: values.stateProvince,
        agentRepresents: values.agentRepresents || null,
        brokerageId: values.brokerageId || null,
        isDefault: values.isDefault,
        categories: values.categories,
      };

      if (isEditMode && existingTemplate?.id) {
        await updateComplianceTemplateInDb(existingTemplate.id, templateData);
        toast.success(`Template "${values.name}" updated successfully`);
        setFormMessage(`Template "${values.name}" updated successfully`);
      } else {
        if (currentUserProfile?.userId) {
          templateData.userId = currentUserProfile.userId;
        }
        if (currentUserProfile?.role === "manager" && currentUserProfile?.userId) {
          templateData.managerId = currentUserProfile.userId;
        }
        await addComplianceTemplateToDb(templateData);
        toast.success(`Template "${values.name}" created successfully`);
        setFormMessage(`Template "${values.name}" created successfully`);
      }
      setFormMessageType("success");

      setTimeout(() => {
        dispatch(closeModal({ modalType: "AdminComplianceTemplateModal" }));
      }, 1500);
    } catch (error) {
      console.error("Error saving compliance template:", error);
      toast.error(`Error saving template: ${error.message}`);
      setFormMessage(`Error saving template: ${error.message}`);
      setFormMessageType("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      size="large"
      header={
        isEditMode
          ? `Edit Compliance Template`
          : `Create New Compliance Template`
      }
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={Yup.object({
          name: Yup.string().required("Template name is required"),
          stateProvince: Yup.string().required("State/Province is required"),
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty, values, setFieldValue }) => (
          <Form className="ui form">
            <Segment>
              <Grid>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="name"
                      label="Template Name"
                      placeholder="e.g., Colorado Buyer Compliance Checklist"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MySelectInput
                      name="stateProvince"
                      label="State/Province"
                      options={userStateOptions}
                      disabled={lockStateProvince}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MySelectInput
                      name="agentRepresents"
                      label="Agent Represents"
                      options={agentRepresentsOptions}
                      placeholder="Select agent representation"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="brokerageId"
                      label={
                        lockBrokerageId
                          ? "Brokerage ID"
                          : "Brokerage ID (Optional)"
                      }
                      placeholder="Leave empty for state-wide default template"
                      readOnly={lockBrokerageId}
                      style={
                        lockBrokerageId ? { backgroundColor: "#f8f8f8" } : undefined
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
                {!lockBrokerageId && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyCheckbox
                        name="isDefault"
                        label="Is Default Template"
                      />
                      <p style={{ fontSize: "0.9em", color: "#666", marginTop: "8px" }}>
                        Default templates are used when no brokerage-specific template exists
                      </p>
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Divider />
                    <Header size="small" color="blue">
                      Template Categories and Items
                    </Header>
                    <p style={{ fontSize: "0.9em", color: "#666", marginBottom: "15px" }}>
                      Add categories and compliance items for this template.
                    </p>

                    <FieldArray name="categories">
                      {({ push: pushCategory, remove: removeCategory }) => (
                        <div style={{ display: "flex", gap: "20px", minHeight: "400px" }}>
                          {/* Left Panel - Categories List */}
                          <div style={{
                            width: "300px",
                            borderRight: "1px solid #ddd",
                            paddingRight: "15px",
                            display: "flex",
                            flexDirection: "column"
                          }}>
                            <Header size="tiny" color="grey">
                              Categories
                            </Header>
                            {values.categories && values.categories.length > 0 ? (
                              <div style={{ flex: 1, overflowY: "auto" }}>
                                {values.categories.map((category, catIndex) => (
                                  <div
                                    key={catIndex}
                                    style={{
                                      padding: "10px",
                                      marginBottom: "8px",
                                      backgroundColor: selectedCategoryIndex === catIndex ? "#e3f2fd" : "#f5f5f5",
                                      border: selectedCategoryIndex === catIndex ? "2px solid #2185d0" : "1px solid #ddd",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px"
                                    }}
                                    onClick={() => setSelectedCategoryIndex(catIndex)}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <strong>{category.name || `Category ${catIndex + 1}`}</strong>
                                      <div style={{ fontSize: "0.85em", color: "#666" }}>
                                        {category.items?.length || 0} items
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                      <Icon
                                        name="arrow up"
                                        size="small"
                                        link
                                        disabled={catIndex === 0}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (catIndex > 0) {
                                            const reordered = [...values.categories];
                                            const [moved] = reordered.splice(catIndex, 1);
                                            reordered.splice(catIndex - 1, 0, moved);
                                            setFieldValue(
                                              "categories",
                                              reordered.map((c, i) => ({ ...c, order: i + 1 }))
                                            );
                                            setSelectedCategoryIndex(catIndex - 1);
                                          }
                                        }}
                                        style={{
                                          margin: 0,
                                          opacity: catIndex === 0 ? 0.3 : 1,
                                          cursor: catIndex === 0 ? "not-allowed" : "pointer"
                                        }}
                                      />
                                      <Icon
                                        name="arrow down"
                                        size="small"
                                        link
                                        disabled={catIndex === values.categories.length - 1}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (catIndex < values.categories.length - 1) {
                                            const reordered = [...values.categories];
                                            const [moved] = reordered.splice(catIndex, 1);
                                            reordered.splice(catIndex + 1, 0, moved);
                                            setFieldValue(
                                              "categories",
                                              reordered.map((c, i) => ({ ...c, order: i + 1 }))
                                            );
                                            setSelectedCategoryIndex(catIndex + 1);
                                          }
                                        }}
                                        style={{
                                          margin: 0,
                                          opacity: catIndex === values.categories.length - 1 ? 0.3 : 1,
                                          cursor: catIndex === values.categories.length - 1 ? "not-allowed" : "pointer"
                                        }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Message info>
                                <p>No categories yet. Click "Add Category" below.</p>
                              </Message>
                            )}
                            <Button
                              type="button"
                              color="blue"
                              fluid
                              style={{ marginTop: "10px" }}
                              onClick={() => {
                                pushCategory({
                                  name: "",
                                  order: (values.categories?.length || 0) + 1,
                                  items: [],
                                });
                                setSelectedCategoryIndex(values.categories?.length || 0);
                              }}
                            >
                              <Icon name="plus" />
                              Add Category
                            </Button>
                          </div>

                          {/* Right Panel - Category Details and Items */}
                          <div style={{ flex: 1, paddingLeft: "15px", overflowY: "auto" }}>
                            {values.categories && values.categories.length > 0 && selectedCategoryIndex < values.categories.length ? (
                              <div>
                                {/* Category Details */}
                                <Card fluid>
                                  <Card.Content>
                                    <Card.Header>
                                      <Grid>
                                        <Grid.Column width={12}>
                                          Category Details
                                        </Grid.Column>
                                        <Grid.Column width={4} textAlign="right">
                                          <Button
                                            type="button"
                                            size="mini"
                                            color="red"
                                            icon="trash"
                                            onClick={() => {
                                              removeCategory(selectedCategoryIndex);
                                              setSelectedCategoryIndex(Math.max(0, selectedCategoryIndex - 1));
                                            }}
                                          />
                                        </Grid.Column>
                                      </Grid>
                                    </Card.Header>
                                    <Card.Description>
                                      <Grid>
                                        <Grid.Row>
                                          <Grid.Column mobile={16} computer={12}>
                                            <div className="field">
                                              <label>Category Name</label>
                                              <Input
                                                placeholder="e.g., Required Disclosures"
                                                value={values.categories[selectedCategoryIndex].name || ""}
                                                onChange={(e) =>
                                                  setFieldValue(
                                                    `categories.${selectedCategoryIndex}.name`,
                                                    e.target.value
                                                  )
                                                }
                                                fluid
                                              />
                                            </div>
                                          </Grid.Column>
                                          <Grid.Column mobile={16} computer={4}>
                                            <div className="field">
                                              <label>Display Order</label>
                                              <Input
                                                type="number"
                                                placeholder="1"
                                                value={values.categories[selectedCategoryIndex].order || selectedCategoryIndex + 1}
                                                onChange={(e) =>
                                                  setFieldValue(
                                                    `categories.${selectedCategoryIndex}.order`,
                                                    parseInt(e.target.value) || 0
                                                  )
                                                }
                                              />
                                            </div>
                                          </Grid.Column>
                                        </Grid.Row>
                                      </Grid>
                                    </Card.Description>
                                  </Card.Content>
                                </Card>

                                {/* Category Items */}
                                <Header size="tiny" color="grey" style={{ marginTop: "20px" }}>
                                  Category Items
                                </Header>
                                <FieldArray name={`categories.${selectedCategoryIndex}.items`}>
                                  {({ push: pushItem }) => (
                                    <div>
                                      {values.categories[selectedCategoryIndex].items && values.categories[selectedCategoryIndex].items.length > 0 ? (
                                        [...values.categories[selectedCategoryIndex].items]
                                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                                          .map((item, itemIndex) => (
                                                    <Segment key={itemIndex} style={{ marginBottom: "15px", backgroundColor: "#fafafa" }}>
                                                      <Grid>
                                                        <Grid.Row>
                                                          <Grid.Column width={14}>
                                                            <strong>Order {item.order || itemIndex + 1}: {item.name || "(Unnamed)"}</strong>
                                                          </Grid.Column>
                                                          <Grid.Column width={2} textAlign="right">
                                                            <Icon
                                                              name="trash"
                                                              color="red"
                                                              link
                                                              onClick={() => {
                                                                const updatedItems = values.categories[selectedCategoryIndex].items.filter(
                                                                  (i) => i !== item
                                                                );
                                                                setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                              }}
                                                            />
                                                          </Grid.Column>
                                                        </Grid.Row>

                                                        {/* Basic Info */}
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={8}>
                                                            <div className="field">
                                                              <label>Item Name *</label>
                                                              <Input
                                                                placeholder="e.g., Lead-Based Paint Disclosure"
                                                                value={item.name || ""}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, name: e.target.value } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                                fluid
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                          <Grid.Column mobile={16} computer={4}>
                                                            <div className="field">
                                                              <label>Order</label>
                                                              <Input
                                                                type="number"
                                                                placeholder="1"
                                                                value={item.order || itemIndex + 1}
                                                                onChange={(e) => {
                                                                  const newOrder = parseInt(e.target.value) || 0;
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items
                                                                    .map((i) => (i === item ? { ...i, order: newOrder } : i))
                                                                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                          <Grid.Column mobile={16} computer={4}>
                                                            <div className="field">
                                                              <label>Requirement</label>
                                                              <select
                                                                className="ui fluid dropdown"
                                                                value={item.requirement || "required"}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item
                                                                      ? {
                                                                          ...i,
                                                                          requirement: e.target.value,
                                                                          ...(e.target.value !== "conditional" && {
                                                                            conditionalBasedOn: "",
                                                                            conditionalValue: "",
                                                                          }),
                                                                        }
                                                                      : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                              >
                                                                {requirementOptions.map(opt => (
                                                                  <option key={opt.key} value={opt.value}>{opt.text}</option>
                                                                ))}
                                                              </select>
                                                            </div>
                                                          </Grid.Column>
                                                        </Grid.Row>

                                                        {item.requirement === "conditional" && (
                                                          <Grid.Row>
                                                            <Grid.Column mobile={16} computer={8}>
                                                              <div className="field">
                                                                <label>Based on</label>
                                                                <select
                                                                  className="ui fluid dropdown"
                                                                  value={item.conditionalBasedOn || ""}
                                                                  onChange={(e) => {
	                                                                    const conditionalBasedOn = e.target.value;
                                                                    const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                      i === item
                                                                        ? {
                                                                            ...i,
	                                                                            conditionalBasedOn,
                                                                            conditionalValue: "",
                                                                          }
                                                                        : i
                                                                    );
                                                                    setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                  }}
                                                                >
                                                                  <option value="">Select condition type</option>
                                                                  {conditionalBasedOnOptions.map(opt => (
                                                                    <option key={opt.key} value={opt.value}>{opt.text}</option>
                                                                  ))}
                                                                </select>
                                                              </div>
                                                            </Grid.Column>
	                                                            {item.conditionalBasedOn && conditionNeedsValue(item.conditionalBasedOn) && (
                                                              <Grid.Column mobile={16} computer={8}>
                                                                <div className="field">
                                                                  <label>{getConditionalValueLabel(item.conditionalBasedOn)}</label>
                                                                  <select
                                                                    className="ui fluid dropdown"
                                                                    value={item.conditionalValue || ""}
                                                                    onChange={(e) => {
                                                                      const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                        i === item ? { ...i, conditionalValue: e.target.value } : i
                                                                      );
                                                                      setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                    }}
                                                                  >
                                                                    <option value="">Select {getConditionalValueLabel(item.conditionalBasedOn).toLowerCase()}</option>
	                                                                    {getConditionalValueOptions(item.conditionalBasedOn, values.categories, item, conditionalDateOptions).map(opt => (
                                                                      <option key={opt.key} value={opt.value}>{opt.text}</option>
                                                                    ))}
                                                                  </select>
                                                                </div>
                                                              </Grid.Column>
                                                            )}
                                                          </Grid.Row>
                                                        )}
	                                                        {item.requirement === "conditional" && item.conditionalBasedOn && (
	                                                          <Grid.Row>
	                                                            <Grid.Column mobile={16} computer={16}>
	                                                              <Message info size="tiny">
	                                                                <strong>Condition shown to users:</strong>{" "}
	                                                                {getConditionalDisplayText(item, { dateOptions: conditionalDateOptions }) ||
	                                                                  "Select a condition value"}
	                                                              </Message>
	                                                            </Grid.Column>
	                                                          </Grid.Row>
	                                                        )}

                                                        {/* Description */}
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={16}>
                                                            <div className="field">
                                                              <label>Description</label>
                                                              <Input
                                                                placeholder="e.g., Required for properties built before 1978"
                                                                value={item.description || ""}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, description: e.target.value } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                                fluid
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                        </Grid.Row>

                                                        {/* Document Matching */}
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={16}>
                                                            <Header size="tiny" color="grey">Document Matching Criteria</Header>
                                                          </Grid.Column>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={5}>
                                                            <div className="field">
                                                              <label>Document Type</label>
                                                              <Input
                                                                placeholder="e.g., Disclosure"
                                                                value={item.documentType || ""}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, documentType: e.target.value } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                                fluid
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                          <Grid.Column mobile={16} computer={6}>
                                                            <div className="field">
                                                              <label>Document Subtype (Optional)</label>
                                                              <Input
                                                                placeholder="e.g., Lead-Based Paint"
                                                                value={item.documentTypeSubtype || ""}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, documentTypeSubtype: e.target.value } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                                fluid
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                          <Grid.Column mobile={16} computer={5}>
                                                            <div className="field">
                                                              <label>Document Name (Alternative)</label>
                                                              <Input
                                                                placeholder="Alternative matching by name"
                                                                value={item.documentName || ""}
                                                                onChange={(e) => {
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, documentName: e.target.value } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                                fluid
                                                              />
                                                            </div>
                                                          </Grid.Column>
                                                        </Grid.Row>

                                                        {/* Applicability Filters */}
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={16}>
                                                            <Header size="tiny" color="grey">Applicability Filters (leave empty for all)</Header>
                                                          </Grid.Column>
                                                        </Grid.Row>
                                                        <Grid.Row>
                                                          <Grid.Column mobile={16} computer={8}>
                                                            <div className="field">
                                                              <label>Applies to Property Types</label>
                                                              <select
                                                                className="ui dropdown"
                                                                multiple
                                                                value={item.appliesToPropertyTypes || []}
                                                                onChange={(e) => {
                                                                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                                                                  const updatedItems = values.categories[selectedCategoryIndex].items.map((i) =>
                                                                    i === item ? { ...i, appliesToPropertyTypes: selected } : i
                                                                  );
                                                                  setFieldValue(`categories.${selectedCategoryIndex}.items`, updatedItems);
                                                                }}
                                                              >
                                                                {propertyTypeOptions.map(opt => (
                                                                  <option key={opt.key} value={opt.value}>{opt.text}</option>
                                                                ))}
                                                              </select>
                                                              <small style={{ color: "#666" }}>Hold Ctrl/Cmd to select multiple</small>
                                                            </div>
                                                          </Grid.Column>
                                                        </Grid.Row>
                                                      </Grid>
                                                    </Segment>
                                                  ))
                                                ) : (
                                                  <p style={{ color: "#999", fontStyle: "italic" }}>
                                                    No items yet. Click "Add Item" below.
                                                  </p>
                                                )}
                                        <Button
                                          type="button"
                                          size="small"
                                          color="green"
                                          style={{ marginTop: "10px" }}
                                          onClick={() =>
                                            pushItem({
                                              name: "",
                                              description: "",
                                              order: (values.categories[selectedCategoryIndex].items?.length || 0) + 1,
                                              requirement: "required",
                                              documentType: "",
                                              documentTypeSubtype: "",
                                              documentName: "",
                                              conditionalBasedOn: "",
                                              conditionalValue: "",
                                              appliesToPropertyTypes: [],
                                              status: "pending",
                                            })
                                          }
                                        >
                                          <Icon name="plus" />
                                          Add Item
                                        </Button>
                                      </div>
                                    )}
                                  </FieldArray>
                              </div>
                            ) : (
                              <Message info>
                                <Message.Header>No Category Selected</Message.Header>
                                <p>Select a category from the left panel or create a new one.</p>
                              </Message>
                            )}
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Button
                      loading={isSubmitting}
                      disabled={!dirty || isSubmitting}
                      type="submit"
                      size="large"
                      color="blue"
                      content={isEditMode ? "Update Template" : "Create Template"}
                    />
                    <Button
                      type="button"
                      size="large"
                      onClick={() =>
                        dispatch(
                          closeModal({ modalType: "AdminComplianceTemplateModal" })
                        )
                      }
                    >
                      Cancel
                    </Button>
                    {formMessage && (
                      <Message
                        positive={formMessageType === "success"}
                        negative={formMessageType === "error"}
                        style={{ marginTop: "10px" }}
                      >
                        {formMessage}
                      </Message>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Form>
        )}
      </Formik>
    </ModalWrapper>
  );
}


